import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components/macro';
import ModelCards from 'src/client/containers/Cards/Model/ModelCards';
import { useHistory, useRouteMatch } from 'react-router';
import { portalRoot } from 'src/client/helpers/portalRoot';
import { useSelector } from 'src/client/redux';
import { aiModels } from './aiModelData';
import { useUpgradeStrip } from 'src/client/containers/HeaderBar/helpers/useUpgradeStrip';

type RouteParams = {
  modelName: string;
};

type TryMaxMessageData =
  | {
      event: 'iframe_close';
      user_uuid: string;
    }
  | {
      event: 'influencer_switch';
      influencer_slug: string;
      user_uuid: string;
    };

// https://metart.trymax.ai/
// ?api_key=<api_key>
// &influencerSlug=JaneDoe
// &userEmail=test@gmail.com
// &userUUID=<uuid>
// &highlightColor=rgba(41,171,226,1)|rgba(41,171,226,1)

export const ModelsAIChatDemo = () => {
  const { params } = useRouteMatch<RouteParams>();
  const user = useSelector((state) => state.auth.user);
  const history = useHistory();

  // With out turning everything inside this into a context, this doesn't actually
  // react if the user were to close the upgrade strip while the iframe was open.
  // It's a bit janky, but it will work on initial page render.
  // Another thing that can be messed with later if this feature is actually used.
  const [upgradeStripOpen] = useUpgradeStrip();

  const iframeQueryParams = new URLSearchParams({
    api_key: 'ced3cf86-cb55-491d-9969-83d105479a78',
    influencerSlug: modelNameToInfluencerSlug(params.modelName || ''),
    userEmail: user?.email || '',
    userUUID: user?.UUID || '',
    highlightColor: 'rgba(206,171,88,1)|rgba(206,171,88,1)',
    closable: 'true',
  });

  const iframeUrl = `https://metart-chat.trymax.ai/?${iframeQueryParams.toString()}`;

  useEffect(() => {
    window.onmessage = ({ origin, data }: MessageEvent<TryMaxMessageData>) => {
      if (origin === 'https://metart-chat.trymax.ai') {
        if (data.event === 'iframe_close') {
          // Close the iframe
          history.replace('/ai-chat');
        }
      }
    };

    return () => {
      window.onmessage = null;
    };
  }, [history]);

  if (!params.modelName) {
    return <ModelCards models={aiModels} />;
  }

  return (
    <>
      {params.modelName &&
        ReactDOM.createPortal(
          <IframeContainer upgradeStripOpen={upgradeStripOpen}>
            <Iframe src={iframeUrl} allow="microphone; clipboard-write" allowFullScreen />
          </IframeContainer>,
          portalRoot,
        )}
    </>
  );
};

// remove hypen, capitalize words
const modelNameToInfluencerSlug = (modelName: string) => {
  return modelName
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
};

const IframeContainer = styled.div<{ upgradeStripOpen?: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: calc(100vh - 64px);
  background: black;
  z-index: 1000;

  ${({ upgradeStripOpen }) =>
    upgradeStripOpen &&
    css`
      // add 28 px for the upgrade strip
      height: calc(100vh - 92px);
    `}
`;

const Iframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;
